import React, {
  createContext, useContext, useEffect, useState, useMemo
} from 'react';
import proptypes from 'prop-types';
import { useConfigService } from '@thd-nucleus/experience-context';
import { FEATURE_SWITCH_KEYS } from '../constants';

const { node, string, bool } = proptypes;
import {
  params,
  string as StringType,
  arrayOf as ArrayType,
  useDataModel,
  customType,
  shape as ShapeType
} from '@thd-nucleus/data-sources';
import { noop } from '../utils/utils';

const getLogoSrc = (specialBuyType, isDailyDealsEnabled) => {
  const sbotdLogo = isDailyDealsEnabled ? 'Value-Pricing-Daily-Deals.svg' : 'sbotd-badge.svg';
  return (specialBuyType === 'sbotd')
    ? sbotdLogo
    : 'sbotw-badge2.svg';
};

const getSpecialBuyEndpoint = (specialBuyType, isDailyDealsEnabled) => {
  const sbotdUrl = isDailyDealsEnabled ? '/daily-deals' : '/SpecialBuy/SpecialBuyOfTheDay';
  const specialBuyEndpoints = {
    sbotd: sbotdUrl,
    pro: '/SpecialBuy/ProSpecialBuyOfTheWeek',
  };

  return specialBuyEndpoints[specialBuyType];
};

const getIconSrcForCategory = (category, isCondensedLayout) => {
  const imageSize = isCondensedLayout ? '400' : '65';
  return category.image.url.replace('<SIZE>', imageSize);
};
const getCategoryId = ({ name }) => name.replace(/[^A-Za-z]/g, '');

const getFallbackValues = (specialBuyType, isDailyDealsEnabled) => ({
  specialBuyType,
  logoSrc: getLogoSrc(specialBuyType, isDailyDealsEnabled),
  tagline: (specialBuyType === 'sbotd')
    ? 'Today\'s Big Savings All in One Place'
    : 'This Week\'s Big Savings All in One Place',
  endDate: null,
  categories: [],
  selectedCategory: null,
  specialBuyEndpoint: getSpecialBuyEndpoint(specialBuyType, isDailyDealsEnabled),
  isFallbackBanner: true,
});

const isSanitaryCategory = (category) => Boolean(
  category?.name
  && category.tagline
  && category.itemIds?.length
  && category.image?.url
  && !category.name.toLowerCase().includes('backup')
);

export const defaultContextValue = {
  specialBuyType: '',
  logoSrc: '',
  tagline: '',
  startDate: null,
  endDate: null,
  categories: [],
  specialBuyEndpoint: '',
  isFallbackBanner: true,
  isSpecialBuyDataLoading: false,
  isCondensedLayout: false,
  selectedCategory: null,
  setSelectedCategory: noop,
  simpleBanner: false
};
const SpecialBuyContext = createContext(defaultContextValue);
const useSpecialBuy = () => useContext(SpecialBuyContext);

const SpecialBuyProvider = ({ children, specialBuyType, isCondensedLayout, simpleBanner }) => {
  const dealType = specialBuyType === 'sbotd' ? 'DAY' : specialBuyType.toUpperCase();
  const { data, loading } = useDataModel('specialBuyMetadata', {
    ssr: false,
    variables: {
      dealType
    }
  });
  const isDailyDealsEnabled = useConfigService(FEATURE_SWITCH_KEYS.isDailyDealsEnabled);
  const [specialBuyData, setSpecialBuyData] = useState(getFallbackValues(specialBuyType, isDailyDealsEnabled));
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    const categories = (data?.specialBuyMetadata?.categoryMetadata || [])
      .filter(isSanitaryCategory)
      .map((category) => ({
        ...category,
        image: {
          url: getIconSrcForCategory(category, isCondensedLayout)
        },
        categoryId: getCategoryId(category)
      }));

    const isUsableSpecialBuyData = Boolean(
      data?.specialBuyMetadata?.tagline
      && data.specialBuyMetadata.startDate
      && categories.length
    );

    const getNewSpecialBuyValues = (buyTypeValue, dealsFeatureEnabled, categoryList) => ({
      specialBuyType: buyTypeValue,
      tagline: data.specialBuyMetadata.tagline,
      startDate: data.specialBuyMetadata.startDate,
      endDate: data.specialBuyMetadata.endDate,
      categories: categoryList,
      logoSrc: getLogoSrc(buyTypeValue, dealsFeatureEnabled),
      specialBuyEndpoint: getSpecialBuyEndpoint(buyTypeValue, dealsFeatureEnabled),
      isFallbackBanner: false,
    });

    const newSpecialBuyData = isUsableSpecialBuyData
      ? getNewSpecialBuyValues(specialBuyType, isDailyDealsEnabled, categories)
      : getFallbackValues(specialBuyType, isDailyDealsEnabled);

    setSpecialBuyData(newSpecialBuyData);
    setSelectedCategory(categories[0] || null);
  }, [specialBuyType, data, setSpecialBuyData, setSelectedCategory, isCondensedLayout]);

  const contextValue = useMemo(() => ({
    ...specialBuyData,
    isSpecialBuyDataLoading: loading,
    isCondensedLayout,
    simpleBanner,
    selectedCategory,
    setSelectedCategory,
  }), [specialBuyData, loading, isCondensedLayout, simpleBanner, selectedCategory]);

  return (
    <SpecialBuyContext.Provider value={contextValue}>
      {children}
    </SpecialBuyContext.Provider>
  );
};

SpecialBuyProvider.propTypes = {
  children: node.isRequired,
  specialBuyType: string.isRequired,
  isCondensedLayout: bool.isRequired,
  simpleBanner: bool
};

SpecialBuyProvider.defaultProps = {
  simpleBanner: false
};

SpecialBuyProvider.displayName = 'SpecialBuyProvider';

SpecialBuyProvider.dataModel = {
  specialBuyMetadata: params({
    dealType: customType('DealType').enum(['DAY', 'PRO'], 'DAY'),
  })
    .shape({
      endDate: StringType(),
      startDate: StringType(),
      tagline: StringType(),
      categoryMetadata: ArrayType({
        itemIds: ArrayType(StringType()),
        tagline: StringType(),
        name: StringType(),
        image: ShapeType({
          url: StringType()
        })
      })
    })
};
export { SpecialBuyProvider, SpecialBuyContext, useSpecialBuy };
